import {useLocalStorage} from 'platform/components';
import {z} from 'zod';

import {useEffect, useMemo} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetAfterSalesAuthorizationProfilesQuery,
  useGetCurrentUserInfoQuery,
  useGetSalesAuthorizationProfilesQuery,
} from '@dms/api/customer';
import {SELECTED_AUTH_PROFILE_KEY, useGetCurrentBranch} from '@dms/shared';

import {isNotNilOrEmpty} from 'shared/web';

const selectedAuthProfileSchema = z.object({
  authorizationProfileId: z.string(),
  authorizationId: z.string(),
});

type SelectedAuthProfile = z.infer<typeof selectedAuthProfileSchema>;

export const useSelectedAuthProfile = () => {
  const [selectedAuthProfile, setSelectedAuthProfile] = useLocalStorage<SelectedAuthProfile | null>(
    SELECTED_AUTH_PROFILE_KEY,
    null
  );

  const {activeBranchId} = useGetCurrentBranch();
  const {data: currentUserInfo} = useGetCurrentUserInfoQuery();

  const {
    data: afterSalesAuthorizationProfiles,
    isLoading: isAfterSalesAuthorizationProfilesLoading,
    isError: isAfterSalesAuthorizationProfilesError,
  } = useGetAfterSalesAuthorizationProfilesQuery(
    {branchId: activeBranchId, userId: currentUserInfo?.id ?? ''},
    {skip: isNil(currentUserInfo?.id) || isNil(activeBranchId)}
  );
  const {
    data: salesAuthorizationProfiles,
    isLoading: isSalesAuthorizationProfilesLoading,
    isError: isSalesAuthorizationProfilesError,
  } = useGetSalesAuthorizationProfilesQuery(
    {branchId: activeBranchId, userId: currentUserInfo?.id ?? ''},
    {skip: isNil(currentUserInfo?.id) || isNil(activeBranchId)}
  );

  const isLoading = isAfterSalesAuthorizationProfilesLoading || isSalesAuthorizationProfilesLoading;

  const isError = isAfterSalesAuthorizationProfilesError || isSalesAuthorizationProfilesError;

  const profileOptions = useMemo(
    () => [...(afterSalesAuthorizationProfiles ?? []), ...(salesAuthorizationProfiles ?? [])],
    [afterSalesAuthorizationProfiles, salesAuthorizationProfiles]
  );
  const hasNoProfiles = isNilOrEmpty(profileOptions);

  useEffect(
    function validateSelectedAuthProfile() {
      const areOptionsValid = isNotNilOrEmpty(profileOptions);
      const parsedSelectedAuthProfile = selectedAuthProfileSchema.safeParse(selectedAuthProfile);

      if (isLoading) {
        return;
      }

      if (!parsedSelectedAuthProfile.success) {
        setSelectedAuthProfile(null);
        return;
      }

      const selectedProfile = profileOptions.find(
        (profile) => profile.authorizationProfileId === selectedAuthProfile?.authorizationProfileId
      );
      const selectedAuthorization = profileOptions.find(
        (profile) => profile.authorizationId === selectedAuthProfile?.authorizationId
      );

      if (areOptionsValid && isNotNil(selectedProfile)) {
        setSelectedAuthProfile({
          authorizationId: selectedProfile.authorizationId,
          authorizationProfileId: selectedProfile.authorizationProfileId,
        });
        return;
      }

      if (areOptionsValid && isNotNil(selectedAuthorization)) {
        setSelectedAuthProfile({
          authorizationId: selectedAuthorization.authorizationId,
          authorizationProfileId: selectedAuthorization.authorizationProfileId,
        });
        return;
      }

      if (areOptionsValid && isNil(selectedProfile) && isNil(selectedAuthorization)) {
        setSelectedAuthProfile(null);
        return;
      }

      // disabling to prevent loop
    },
    // eslint-disable-next-line react/exhaustive-deps
    [profileOptions, isLoading]
  );

  return {
    isLoading,
    isError,
    hasNoProfiles,
    profileOptions,
    selectedAuthProfile,
    setSelectedAuthProfile,
  };
};
